import { html } from 'lit-element';
import moment from 'moment';

import connect from '@elab/core/store/connect';
import { LightLitElement } from '@elab/components';
import slotsUtils from '@elab/core/utils/ui/slotsUtils';
import {
    COMPONENT_CONNECT,
    COMPONENT_DISCONNECT,
    createDispatcher,
} from '@elab/core/utils/dynaReduxHelpers';
import { datePickerConstants } from '@elab/core/utils/constants';

import {
    DATE_FROM,
    DATE_TO,
    actions,
    reducers,
    sagaFactory,
    selectors,
    storeName,
    types,
} from './redux';

/**
 * Component is used to show user's loyalty card history
 *
 * @prop {Number} pageSize - the number of records per page
 * @prop {String} earnedLabel - the label for earned points
 * @prop {String} pointsLabel - name of the loyalty points displayed after the amount of them
 *
 * @slot header - header content
 * @slot empty-list-message - the message that is shown when the history is empty
 * @slot show-more-label - show-more button label
 * @slot disclaimer - disclaimer text placed at the bottom of the component
 *
 * Example of usage
 * <e2-loyalty-card-history
 *    page-size="4"
 *    earned-label="You earned"
 *    points-label="points"
 * >
 *   <h3 v-slot="header">Title</h3>
 *   <span v-slot="empty-list-message">the list if empty</span>
 *   <div v-slot="show-more-label">show more</div>
 *   <div v-slot="disclaimer">disclaimer</div>
 * </e2-loyalty-card-history>
 */

class LoyaltyCardHistory extends LightLitElement {
    static get properties() {
        return {
            pageSize: { type: Number, attribute: 'page-size' },

            // labels
            earnedLabel: { type: String, attribute: 'earned-label' },
            redeemedLabel: { type: String, attribute: 'redeemed-label' },
            showRedeemed: { type: Boolean, attribute: 'show-redeemed' },
            pointsLabel: { type: String, attribute: 'points-label' },
            filterLabel: { type: String, attribute: 'filter-label' },
            fromLabel: { type: String, attribute: 'from-label' },
            toLabel: { type: String, attribute: 'to-label' },
            weekDayLabels: { type: String, attribute: 'week-day-labels' },
            monthLabels: { type: String, attribute: 'month-labels' },
            resetLabel: { type: String, attribute: 'reset-label' },
            cancelLabel: { type: String, attribute: 'cancel-label' },
            submitLabel: { type: String, attribute: 'submit-label' },
            showMoreClassModifier: { type: String, attribute: 'show-more-class-modifier' },
            showMoreButtonSize: { type: String, attribute: 'show-more-button-size' },
            hideHelpIcon: { type: Boolean, attribute: 'hide-help-icon' },
            // redux
            records: { type: Array, attribute: false },
            canShowMore: { type: Boolean, attribute: false },
            dateFrom: { type: Number, attribute: false },
        };
    }

    constructor() {
        super();
        this.records = [];
        this.filterLabel = '';
        this.fromLabel = '';
        this.toLabel = '';
        this.showMoreClassModifier = '';
        this.showMoreButtonSize = 'big';
        this.showRedeemed = false;
        this.hideHelpIcon = false;
    }

    afterConnect() {
        this.dispatchEvent(new CustomEvent(COMPONENT_CONNECT));
    }

    beforeDisconnect() {
        this.dispatchEvent(new CustomEvent(COMPONENT_DISCONNECT));
    }

    firstUpdated() {
        slotsUtils.emulateSlots(this);
    }

    updated(changedProperties) {
        if (changedProperties.has('pageSize')) {
            this.dispatchEvent(
                new CustomEvent('SET_PAGE_SIZE', {
                    detail: this.pageSize,
                }),
            );
        }
    }

    getFilteringSectionTemplate = () => {
        return html`
            <div class="loyalty-card-history__filter-label">${this.filterLabel}</div>
            <div class="loyalty-card-history__filter-fields">
                <span
                    class="loyalty-card-history__filter-fields-item loyalty-card-history__from-calendar"
                >
                    <e2-datepicker-calendar
                        class="field-wrapper field-wrapper--loyalty-history"
                        name="${DATE_FROM}"
                        store-name="loyalty-card-history"
                        left-date-offset="-1826"
                        right-date-offset="0"
                        reset-button-label="${this.resetLabel}"
                        cancel-button-label="${this.cancelLabel}"
                        submit-button-label="${this.submitLabel}"
                        week-day-labels=${this.weekDayLabels}
                        month-labels=${this.monthLabels}
                        is-clean-value
                        is-modal
                    >
                        <div v-slot="default-label">
                            <div class="loyalty-card-history__field-label-wrapper">
                                <div class="loyalty-card-history__field-label">
                                    ${this.fromLabel}
                                </div>
                            </div>
                        </div>
                        <div v-slot="icon"><i class="loyalty-card-history__filter-arrow"></i></div>
                    </e2-datepicker-calendar>
                </span>
                <span
                    class="loyalty-card-history__filter-fields-item loyalty-card-history__to-calendar"
                >
                    <e2-datepicker-calendar
                        class="field-wrapper field-wrapper--loyalty-history"
                        name="${DATE_TO}"
                        store-name="loyalty-card-history"
                        left-date-offset="${moment(
                            this.dateFrom,
                            datePickerConstants.DEFAULT_FORMAT,
                        ).diff(moment(), 'days')}"
                        right-date-offset="0"
                        reset-button-label="${this.resetLabel}"
                        cancel-button-label="${this.cancelLabel}"
                        submit-button-label="${this.submitLabel}"
                        week-day-labels=${this.weekDayLabels}
                        month-labels=${this.monthLabels}
                        is-clean-value
                        is-modal
                    >
                        <div v-slot="default-label">${this.toLabel}</div>
                        <div v-slot="icon"><i class="loyalty-card-history__filter-arrow"></i></div>
                    </e2-datepicker-calendar>
                </span>
            </div>
        `;
    };

    getHistoryRecordTemplate = record => {
        return html`
            <li class="loyalty-card-history__item loyalty-card-record">
                <div class="loyalty-card-record__section loyalty-card-record__section--main">
                    <span class="loyalty-card-record__date">
                        ${moment(record.date).format('DD/MM/YY')}
                    </span>
                    <span class="loyalty-card-record__points">
                        <span class="loyalty-card-record__points--value">
                            ${this.earnedLabel}
                        </span>
                        <span class="loyalty-card-record__points--marked">
                            ${record.pointsEarned} ${this.pointsLabel}
                        </span>
                    </span>
                </div>
                ${this.showRedeemed
                    ? html`
                          <div
                              class="loyalty-card-record__section loyalty-card-record__section--redeemed"
                          >
                              <span class="loyalty-card-record__points">
                                  <span class="loyalty-card-record__points--value">
                                      ${this.redeemedLabel}
                                  </span>
                                  <span class="loyalty-card-record__points--marked">
                                      ${record.pointsRedeemed} ${this.pointsLabel}
                                  </span>
                              </span>
                          </div>
                      `
                    : ''}
                <div class="loyalty-card-record__section loyalty-card-record__section--store-name">
                    <span class="loyalty-card-record__store-name">
                        ${record.storeName}
                    </span>
                </div>
                ${this.showHelpIcon()}
            </li>
        `;
    };

    showHelpIcon() {
        this.hideHelpIcon
            ? html``
            : html`
                  <div class="loyalty-card-record__section loyalty-card-record__section--icon">
                      <i class="loyalty-card-record__icon"></i>
                  </div>
              `;
    }

    getShowMoreButtonTemlate = () => {
        return html`
            <e2-action-button
                class="loyalty-card-history__show-more ${this.records.length && this.canShowMore
                    ? 'd-contents'
                    : 'd-none'}"
                no-arrow
                action-type="${types.SHOW_MORE}"
                class-modifier="${this.showMoreClassModifier}"
                size="${this.showMoreButtonSize}"
            >
                <v-slot name="show-more-label" class="emulate-slot"></v-slot>
            </e2-action-button>
        `;
    };

    render() {
        return html`
            <div class="loyalty-card-history">
                <v-slot name="header"></v-slot>
                <section class="loyalty-card-history__filter">
                    ${this.getFilteringSectionTemplate()}
                </section>
                <section class="loyalty-card-history__main">
                    <ul class="loyalty-card-history__list">
                        ${this.records.map(this.getHistoryRecordTemplate)}
                    </ul>
                    ${this.getShowMoreButtonTemlate()}
                    <v-slot
                        name="empty-list-message"
                        class="${!this.records.length ? 'd-contents' : 'd-none'}"
                    ></v-slot>
                </section>
                <v-slot name="disclaimer"></v-slot>
            </div>
        `;
    }
}

const mapDispatchToEvents = dispatch =>
    createDispatcher(
        {
            storeName,
            reducers,
            sagaFactory,
            additionalEvents: {
                SET_PAGE_SIZE: ({ detail }) => dispatch(actions.setPageSize(detail)),
            },
        },
        dispatch,
    );

const mapStateToProps = state => {
    return {
        records: selectors.getRecords(state),
        canShowMore: selectors.getFurtherAvailabilityState(state),
        dateFrom: selectors.getDataFieldValue(state, DATE_FROM),
    };
};

export default () =>
    customElements.define(
        'e2-loyalty-card-history',
        connect(mapStateToProps, mapDispatchToEvents)(LoyaltyCardHistory),
    );
